// Geteilte Konstanten, Typen und Helfer fuer die Konzern-Konsolidierung der
// integrierten Planung (verwendet von ConsolidatedMatrix,
// PlanEditorPage und den Verwaltungs-Modals).

export const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
export const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
export const SECTION_LABEL: Record<string, string> = {
  guv: 'Gewinn- und Verlustrechnung (BWA)',
  bilanz: 'Bilanz',
  cashflow: 'Cashflow-Ableitung (Indirekt)',
  liqui: 'Liquiditätsentwicklung',
}
export const SECTION_ORDER = ['guv', 'bilanz', 'cashflow', 'liqui']

export interface Company { key: string; name: string }
export interface Group { id: number; name: string; year: number; memberKeys: string[]; eliminate: boolean }
export interface MatrixRow {
  code: string
  label: string
  section: string
  isAggregate: boolean
  parentCode: string | null
  sortOrder: number
  totals: number[]
  elimination: number[]
  netTotals: number[]
}
export interface MatrixMember { companyKey: string; planId: number | null; found: boolean }
export interface MatrixResponse {
  groupId: number
  name: string
  year: number
  eliminate: boolean
  members: MatrixMember[]
  matrix: MatrixRow[]
}

export type ViewMode = 'net' | 'gross' | 'elim'
export const VIEW_LABEL: Record<ViewMode, string> = {
  net: 'Konzern (netto)',
  gross: 'Summe (brutto)',
  elim: './. Elimination',
}
export function rowValues(r: MatrixRow, mode: ViewMode): number[] {
  return mode === 'gross' ? r.totals : mode === 'elim' ? r.elimination : r.netTotals
}

export function yearAggregate(code: string, totals: number[]): number {
  // Bilanz = Bestand -> Dezember; Anfangsbestand -> Januar; sonst Summe.
  if (code.startsWith('bs_') || code === 'liq_endbestand') return totals[11] ?? 0
  if (code === 'liq_anfangsbestand') return totals[0] ?? 0
  return totals.reduce((s, v) => s + v, 0)
}

// ── Intercompany ────────────────────────────────────────────────────────────
export type IcEffect = 'pnl' | 'finance'
export interface IcLinkRow {
  id: number
  year: number
  label: string
  kind: string
  effect: IcEffect
  providerKey: string
  recipientKey: string
  providerPosition: string
  recipientPosition: string
  amountMonthly: number
  startMonth: number
  endMonth: number | null
  active: boolean
}

export const IC_KINDS = ['miete', 'darlehen', 'verrechnung', 'dienstleistung', 'ausschuettung', 'einlage', 'sonstiges'] as const
export const IC_KIND_LABELS: Record<string, string> = {
  miete: 'Miete',
  darlehen: 'Gesellschafterdarlehen',
  verrechnung: 'Verrechnung / Management Fee',
  dienstleistung: 'Dienstleistung',
  ausschuettung: 'Gewinnausschüttung',
  einlage: 'Kapitaleinlage',
  sonstiges: 'Sonstiges',
}
// Arten ohne GuV-Wirkung (liquiditaets-/eigenkapitalwirksam).
export const FINANCE_KINDS = new Set(['darlehen', 'ausschuettung', 'einlage'])
export function effectForKind(kind: string): IcEffect {
  return FINANCE_KINDS.has(kind) ? 'finance' : 'pnl'
}

export const ERTRAG_POSITIONS = [
  { code: 'sonstige_ertraege', label: 'Sonstige Erträge' },
  { code: 'ums_dienstleistungen', label: 'voraussichtliche Dienstleistungen' },
  { code: 'ums_vertraege', label: 'Verträge' },
]
export const AUFWAND_POSITIONS = [
  { code: 'raumkosten', label: 'Raumkosten' },
  { code: 'sonstige_kosten', label: 'Sonstige Kosten' },
  { code: 'kfz_kosten', label: 'Kfz-Kosten' },
  { code: 'werbe_reisekosten', label: 'Werbe-/Reisekosten' },
  { code: 'reparaturen', label: 'Reparaturen' },
]
